// src/components/AdminCentral/Other/WebinarContent.js
import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, "0")
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);
const MERIDIAN_OPTIONS = ["am", "pm"];

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Actions = styled.div`
  ${actionContainerStyles};
  // border: 1px solid ${({ colors }) => colors.border};

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }
  li + li {
    margin-top: 4px;
  }
  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  padding: 20px;
  background-color: '#fff' !important;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
  background-color: '#fff';

  label {
    font-size: 14px;
    margin-right: 8px;
    color: #000;
    font-weight: 'bold';
  }

  select,
  input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }

  input {
    width: 200px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;
  background-color: '#fff';

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 10px;
  }

  a {
    color: ${({ colors }) => colors.buttonBackground};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RegButton = styled.button`
  display: inline-block;
  padding: 4px 8px;
  background-color: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const TitleButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: ${({ colors }) => colors.buttonBackground};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid ${({ colors }) => colors.border};
    background-color: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

const LegacyOuter = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const LegacyHeading = styled.h1`
  text-align: left;
  font-family: 'Source Sans Pro', sans-serif;
  line-height: 20pt;
  padding: 0;
  color: ${({ colors }) => colors?.labelText || "#2C4960"};
  font-weight: normal;
  margin: 0 0 20px;
  font-size: 24px;
`;

const LegacyForm = styled.form`
  text-align: left;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  margin: 0;
  clear: both;
  padding: 0;
  line-height: 20pt;
  background: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const LegacyTable = styled.table`
  text-align: left;
  line-height: 20pt;
  margin: 0;
  padding: 0;
  background: #f5f5f5;
  border-right: 0;
  clear: both;
  color: #333;
  width: 100%;
  border-spacing: 0;
  font-size: 12px;
  empty-cells: show;
  font-family: 'Source Sans Pro', sans-serif;
`;

const LegacyRow = styled.tr`
  text-align: left;
  line-height: 20pt;
  color: #333;
  border-spacing: 0;
  font-size: 12px;
  empty-cells: show;
  font-family: 'Source Sans Pro', sans-serif;
  margin: 0;
  padding: 0;
  border-bottom: 1px solid #dddddd;

  &:last-child {
    border-bottom: none;
  }
`;

const LegacyCell = styled.td`
  line-height: 20pt;
  color: #333;
  border-spacing: 0;
  font-size: 12px;
  empty-cells: show;
  font-family: 'Source Sans Pro', sans-serif;
  margin: 0;
  padding: 16px 20px;
  vertical-align: middle;
  text-align: left;
  background: #f5f5f5;
`;

const LegacyInputGroup = styled.div`
  display: flex;
  flex-direction: column;

  label,
  .groupLabel {
    margin-bottom: 6px;
    font-weight: 600;
    color: ${({ colors }) => colors?.labelText || "#2C4960"};
  }

  input:not([type="checkbox"]),
  textarea,
  select {
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 13px;
    padding: 6px 8px;
    border: 1px solid ${({ colors }) => colors?.border || "#ccc"};
    border-radius: 4px;
    width: 100%;
  }

  input[type="date"] {
    background-color: #fff;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;
    color-scheme: light;
  }

  input[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    color: transparent;
    cursor: pointer;
  }

  input[type="date"]::-webkit-datetime-edit {
    padding: 0;
  }

  textarea {
    resize: vertical;
  }
`;

const LegacyCheckboxGroup = styled(LegacyInputGroup)`
  flex-direction: row;
  align-items: center;
  gap: 8px;

  label {
    margin-bottom: 0;
    font-weight: 600;
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const LegacyTimeInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
`;

const LegacyTimeControl = styled.label`
  display: flex;
  flex-direction: column;
  font-weight: 600;
  color: ${({ colors }) => colors?.labelText || "#2C4960"};

  span {
    margin-bottom: 6px;
  }

  select {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid ${({ colors }) => colors?.border || "#ccc"};
    border-radius: 4px;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 13px;
    min-width: 160px;
  }
`;

const LegacySubmitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid #dddddd;
  justify-content: flex-start;

  input[type="submit"] {
    padding: 8px 16px;
    background: ${({ colors }) => colors?.buttonBackground || "#2C4960"};
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-family: 'Source Sans Pro', sans-serif;
  }
`;

const LegacyCancelLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
  font-family: 'Source Sans Pro', sans-serif;
  color: ${({ colors }) => colors?.buttonBackground || "#2C4960"};
  text-decoration: underline;
  cursor: pointer;
`;

export default function WebinarContent({ colors }) {
  const [loading, setLoading] = useState(true);
  const [webinars, setWebinars] = useState([]);

  // which view? 'list' | 'add' | 'regs'
  const [view, setView] = useState("list");
  // registrations
  const [regs, setRegs] = useState([]);
  // current webinar for regs view
  const [currentWebinar, setCurrentWebinar] = useState(null);

  // list controls
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // add form state
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("09");
  const [minute, setMinute] = useState("00");
  const [meridian, setMeridian] = useState("am");
  const [maxAttendee, setMaxAttendee] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [isCancelled, setIsCancelled] = useState(false);
  const [webinarId, setWebinarId] = useState(null);
  const [currentAttendee, setCurrentAttendee] = useState(0);
  const [editTarget, setEditTarget] = useState(null);

  // confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  // load webinars
  const loadWebinars = async () => {
    setLoading(true);
    try {
      const resp = await API.getWebinars({});
      const list = Array.isArray(resp.result) ? resp.result : [];
      const sorted = [...list].sort((a, b) => {
        const aTime = new Date(a.scheduled || a.date || 0).getTime();
        const bTime = new Date(b.scheduled || b.date || 0).getTime();
        if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
        if (Number.isNaN(aTime)) return 1;
        if (Number.isNaN(bTime)) return -1;
        return bTime - aTime;
      });
      setWebinars(sorted);
    } catch (err) {
      console.error("Failed to load webinars:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebinars();
  }, []);

  const resetFormState = () => {
    setName("");
    setDate("");
    setHour("09");
    setMinute("00");
    setMeridian("am");
    setMaxAttendee("");
    setMeetingLink("");
    setAccessCode("");
    setPhoneNumber("");
    setNotes("");
    setIsCancelled(false);
    setWebinarId(null);
    setCurrentAttendee(0);
    setEditTarget(null);
  };

  const parseScheduled = (value) => {
    const fallback = {
      date: "",
      hour: "09",
      minute: "00",
      meridian: "am",
    };

    if (!value) return fallback;
    const trimmed = String(value).trim();
    if (!trimmed) return fallback;

    const isoDateTimeMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
    );
    if (isoDateTimeMatch) {
      const [, yyyy, mm, dd, hh24, min] = isoDateTimeMatch;
      const hour24 = Number(hh24);
      const minuteVal = min;
      const meridianVal = hour24 >= 12 ? "pm" : "am";
      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;
      return {
        date: `${yyyy}-${mm}-${dd}`,
        hour: String(hour12).padStart(2, "0"),
        minute: String(minuteVal).padStart(2, "0"),
        meridian: meridianVal,
      };
    }

    const parsedDate = new Date(trimmed);
    if (!Number.isNaN(parsedDate.getTime())) {
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      const year = parsedDate.getFullYear();
      let hours24 = parsedDate.getHours();
      const minuteVal = String(parsedDate.getMinutes()).padStart(2, "0");
      const meridianVal = hours24 >= 12 ? "pm" : "am";
      let hour12 = hours24 % 12;
      if (hour12 === 0) hour12 = 12;
      return {
        date: `${year}-${month}-${day}`,
        hour: String(hour12).padStart(2, "0"),
        minute: minuteVal,
        meridian: meridianVal,
      };
    }

    const manualMatch = trimmed.match(
      /^(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}):(\d{2})\s*(am|pm)?/i
    );
    if (manualMatch) {
      const [, manualDate, rawHour, rawMinute, rawMeridian] = manualMatch;
      const [mm, dd, yyyy] = manualDate.split("/");
      const isoDate = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const meridianVal = rawMeridian ? rawMeridian.toLowerCase() : "am";
      const hourNum = Number(rawHour);
      return {
        date: isoDate,
        hour: String(hourNum || 12).padStart(2, "0"),
        minute: String(rawMinute).padStart(2, "0"),
        meridian: meridianVal,
      };
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      return {
        ...fallback,
        date: trimmed,
      };
    }

    return fallback;
  };

  const initializeAddView = () => {
    resetFormState();
    setView("add");
  };

  const initializeEditView = (webinar) => {
    resetFormState();
    if (!webinar) {
      setView("list");
      return;
    }
    const scheduleParts = parseScheduled(webinar.scheduled);
    setName(webinar.name || "");
    setDate(scheduleParts.date);
    setHour(scheduleParts.hour);
    setMinute(scheduleParts.minute);
    setMeridian(scheduleParts.meridian);
    setMaxAttendee(
      webinar.max_attendee !== undefined && webinar.max_attendee !== null
        ? String(webinar.max_attendee)
        : ""
    );
    setMeetingLink(webinar.meeting_link || "");
    setAccessCode(webinar.access_code || "");
    setPhoneNumber(webinar.phone_number || webinar.phone || "");
    setNotes(webinar.notes || "");
    setIsCancelled(
      webinar.is_cancelled === 1 ||
        webinar.is_cancelled === "1" ||
        webinar.is_cancelled === true
    );
    setWebinarId(webinar.webinar_id);
    setCurrentAttendee(Number(webinar.curr_attendee || 0));
    setEditTarget(webinar);
    setView("edit");
  };

  const handleCancelEdit = () => {
    resetFormState();
    setView("list");
  };

  // filter + paginate for list view
  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return webinars.filter(w =>
      w.name.toLowerCase().includes(t) ||
      (w.meeting_link || "").toLowerCase().includes(t) ||
      w.scheduled.toLowerCase().includes(t)
    );
  }, [webinars, searchTerm]);

  const pageCount = Math.ceil(filtered.length / pageSize);
  const currentData =
    pageSize === -1
      ? filtered
      : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const normalizeDateForApi = (value) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, mm, dd, yyyy] = slashMatch;
      return [
        yyyy,
        mm.padStart(2, "0"),
        dd.padStart(2, "0"),
      ].join("-");
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const yyyy = parsed.getFullYear();
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const dd = String(parsed.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    return "";
  };

  // build datetime
  const buildDateTime = () => {
    const normalized = normalizeDateForApi(date);
    if (!normalized) return "";
    let hh = parseInt(hour, 10);
    if (meridian === "pm" && hh < 12) hh += 12;
    if (meridian === "am" && hh === 12) hh = 0;
    const hhStr = hh.toString().padStart(2, "0");
    const mmStr = minute.padStart(2, "0");
    return `${normalized} ${hhStr}:${mmStr}:00`;
  };

  // handle create
  const handleCreate = async () => {
    setModalStatus("Success");
    try {
      const resp = await API.addWebinar({
        name,
        date: buildDateTime(),
        max_attendee: Number(maxAttendee),
        meeting_link: meetingLink,
        access_code: accessCode,
        phone_number: phoneNumber,
        notes,
      });
      setModalTitle(resp?.status === "Failed" ? "Error" : "Success");
      setModalMessage(resp?.message || "Webinar created.");
      setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
      setResultOpen(true);
      await loadWebinars();
      resetFormState();
      setView("list");
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Failed to create webinar.");
      setModalStatus("Failed");
      setResultOpen(true);
    }
  };

  const handleEdit = async () => {
    if (!webinarId) return;
    setModalStatus("Success");
    try {
      const resp = await API.editWebinar({
        webinar_id: webinarId,
        name,
        date: buildDateTime(),
        max_attendee: Number(maxAttendee),
        meeting_link: meetingLink,
        access_code: accessCode,
        phone_number: phoneNumber,
        notes,
        is_cancelled: isCancelled ? 1 : 0,
        curr_attendee: currentAttendee,
      });
      setModalTitle(resp?.status === "Failed" ? "Error" : "Success");
      setModalMessage(resp?.message || "Webinar updated.");
      setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
      setResultOpen(true);
      await loadWebinars();
      handleCancelEdit();
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Failed to update webinar.");
      setModalStatus("Failed");
      setResultOpen(true);
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (confirmAction === "create") {
      handleCreate();
    } else if (confirmAction === "edit") {
      handleEdit();
    }
    setConfirmAction(null);
    setConfirmMessage("");
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  // handle "Registered" click
  const handleViewRegs = async (web) => {
    setLoading(true);
    setCurrentWebinar(web);
    try {
      const resp = await API.getWebinarRegistered({ webinar_id: web.webinar_id });
      setRegs(resp.result || []);
      setView("regs");
    } catch (err) {
      console.error("Failed to load registrations:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p>Loading…</p>;

  return (
    <Container>
      {/* confirm before create */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm"
        message={confirmMessage || "Are you sure?"}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmAction(null);
          setConfirmMessage("");
        }}
        onConfirm={handleConfirm}
      />
      {/* result */}
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      <ResponsiveActions
        colors={colors}
        renderActions={() => (
          <Actions colors={colors}>
            <h3>Actions</h3>
            <ul>
              {view === "list" && (
                <li>
                  <a onClick={initializeAddView}>Add New</a>
                </li>
              )}
              {(view === "add" || view === "regs" || view === "edit") && (
                <li>
                  <a
                    onClick={() => {
                      resetFormState();
                      setView("list");
                    }}
                  >
                    Webinar List
                  </a>
                </li>
              )}
            </ul>
          </Actions>
        )}
      />

      <View>
        {view === "list" && (
          <>
            <ControlsContainer colors={colors}>
              <div>
                <label>Show</label>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[25, 50, 100, -1].map(n => (
                    <option key={n} value={n}>
                      {n === -1 ? "All" : n}
                    </option>
                  ))}
                </select>
                <label style={{ marginLeft: 5 }}>entries</label>
              </div>
              <div>
                <label>Search:</label>
                <input
                  type="text"
                  placeholder="Filter..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </ControlsContainer>

            <TableWrapper>
              <Table colors={colors}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Attendees</th>
                    <th>Link</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map(w => (
                    <tr key={w.webinar_id}>
                      <td>
                        <TitleButton
                          type="button"
                          colors={colors}
                          onClick={() => initializeEditView(w)}
                        >
                          {w.name}
                        </TitleButton>
                      </td>
                      <td>{w.scheduled}</td>
                      <td>
                        {w.curr_attendee} / {w.max_attendee}
                      </td>
                      <td>
                        {w.meeting_link ? (
                          <a href={w.meeting_link} target="_blank" rel="noreferrer">
                            {w.meeting_link}
                          </a>
                        ) : ""}
                      </td>
                      <td>
                        <RegButton
                          colors={colors}
                          onClick={() => handleViewRegs(w)}
                        >
                          Registered
                        </RegButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

            <InfoText colors={colors}>
              Showing{" "}
              {currentData.length
                ? (currentPage - 1) * pageSize + 1
                : 0}{" "}
              to{" "}
              {pageSize === -1
                ? filtered.length
                : Math.min(currentPage * pageSize, filtered.length)}{" "}
              of {filtered.length} entries
            </InfoText>

            <Pagination colors={colors}>
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, pageCount))}
                disabled={pageSize === -1 || currentPage === pageCount}
              >
                Next
              </button>
            </Pagination>
          </>
        )}

        {view === "add" && (
          <LegacyOuter>
            <LegacyHeading colors={colors}>Create New Webinar</LegacyHeading>
            <LegacyForm
              colors={colors}
              onSubmit={(e) => {
                e.preventDefault();
                setConfirmAction("create");
                setConfirmMessage("Create this webinar?");
                setConfirmOpen(true);
              }}
            >
              <LegacyTable>
                <tbody>
                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-name">Webinar Title:</label>
                        <input
                          id="create-webinar-name"
                          type="text"
                          required
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-date">Date</label>
                        <input
                          id="create-webinar-date"
                          type="date"
                          required
                          value={date}
                          onChange={(event) => setDate(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <span className="groupLabel">Time (Pacific Time)</span>
                        <LegacyTimeInputs colors={colors}>
                          <LegacyTimeControl colors={colors}>
                            <select
                              id="create-webinar-hour"
                              required
                              value={hour}
                              onChange={(event) => setHour(event.target.value)}
                            >
                              {HOUR_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {Number(option)}
                                </option>
                              ))}
                            </select>
                          </LegacyTimeControl>
                          <LegacyTimeControl colors={colors}>
                            <select
                              id="create-webinar-minute"
                              required
                              value={minute}
                              onChange={(event) => setMinute(event.target.value)}
                            >
                              {MINUTE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </LegacyTimeControl>
                          <LegacyTimeControl colors={colors}>
                            <select
                              id="create-webinar-meridian"
                              required
                              value={meridian}
                              onChange={(event) => setMeridian(event.target.value)}
                            >
                              {MERIDIAN_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </LegacyTimeControl>
                        </LegacyTimeInputs>
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-max-attendee">
                          Maximum number of attendees
                        </label>
                        <input
                          id="create-webinar-max-attendee"
                          type="text"
                          required
                          value={maxAttendee}
                          onChange={(event) => setMaxAttendee(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-meeting-link">
                          Meeting Link
                        </label>
                        <input
                          id="create-webinar-meeting-link"
                          type="text"
                          required
                          value={meetingLink}
                          onChange={(event) => setMeetingLink(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-access-code">
                          Access Code
                        </label>
                        <input
                          id="create-webinar-access-code"
                          type="text"
                          required
                          value={accessCode}
                          onChange={(event) => setAccessCode(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-phone">
                          Go To Meeting Phone Number
                        </label>
                        <input
                          id="create-webinar-phone"
                          type="text"
                          required
                          value={phoneNumber}
                          onChange={(event) => setPhoneNumber(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="create-webinar-notes">Notes</label>
                        <textarea
                          id="create-webinar-notes"
                          rows={6}
                          value={notes}
                          onChange={(event) => setNotes(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacySubmitRow colors={colors}>
                        <input type="submit" value="Submit" />
                        <LegacyCancelLink
                          type="button"
                          colors={colors}
                          onClick={() => {
                            resetFormState();
                            setView("list");
                          }}
                        >
                          Cancel
                        </LegacyCancelLink>
                      </LegacySubmitRow>
                    </LegacyCell>
                  </LegacyRow>
                </tbody>
              </LegacyTable>
            </LegacyForm>
          </LegacyOuter>
        )}

        {view === "edit" && (
          <LegacyOuter>
            <LegacyHeading colors={colors}>
              Editing: {editTarget?.name || name}
            </LegacyHeading>
            <LegacyForm
              colors={colors}
              onSubmit={(e) => {
                e.preventDefault();
                setConfirmAction("edit");
                setConfirmMessage("Save changes to this webinar?");
                setConfirmOpen(true);
              }}
            >
              <LegacyTable>
                <tbody>
                  <LegacyRow>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-name">Webinar Name:</label>
                        <input
                          id="edit-webinar-name"
                          type="text"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                    <LegacyCell>
                      <LegacyCheckboxGroup colors={colors}>
                        <input
                          id="edit-webinar-cancelled"
                          type="checkbox"
                          checked={isCancelled}
                          onChange={(event) => setIsCancelled(event.target.checked)}
                        />
                        <label htmlFor="edit-webinar-cancelled">
                          Cancel Webinar
                        </label>
                      </LegacyCheckboxGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-date">Date</label>
                        <input
                          id="edit-webinar-date"
                          type="date"
                          value={date}
                          onChange={(event) => setDate(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <span className="groupLabel">Time (Pacific Time)</span>
                        <LegacyTimeInputs colors={colors}>
                          <LegacyTimeControl colors={colors}>
                            <span>Hour</span>
                            <select
                              id="edit-webinar-hour"
                              value={hour}
                              onChange={(event) => setHour(event.target.value)}
                            >
                              {HOUR_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {Number(option)}
                                </option>
                              ))}
                            </select>
                          </LegacyTimeControl>
                          <LegacyTimeControl colors={colors}>
                            <span>Minute</span>
                            <select
                              id="edit-webinar-minute"
                              value={minute}
                              onChange={(event) => setMinute(event.target.value)}
                            >
                              {MINUTE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </LegacyTimeControl>
                          <LegacyTimeControl colors={colors}>
                            <span>AM / PM</span>
                            <select
                              id="edit-webinar-meridian"
                              value={meridian}
                              onChange={(event) => setMeridian(event.target.value)}
                            >
                              {MERIDIAN_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </LegacyTimeControl>
                        </LegacyTimeInputs>
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-max-attendee">
                          Maximum number of attendees
                        </label>
                        <input
                          id="edit-webinar-max-attendee"
                          type="text"
                          value={maxAttendee}
                          onChange={(event) => setMaxAttendee(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-meeting-link">
                          Meeting Link
                        </label>
                        <input
                          id="edit-webinar-meeting-link"
                          type="text"
                          value={meetingLink}
                          onChange={(event) => setMeetingLink(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-access-code">
                          Access Code
                        </label>
                        <input
                          id="edit-webinar-access-code"
                          type="text"
                          value={accessCode}
                          onChange={(event) => setAccessCode(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                    <LegacyCell>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-phone">
                          Go To Meeting Phone Number
                        </label>
                        <input
                          id="edit-webinar-phone"
                          type="text"
                          required
                          value={phoneNumber}
                          onChange={(event) => setPhoneNumber(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacyInputGroup colors={colors}>
                        <label htmlFor="edit-webinar-notes">Notes</label>
                        <textarea
                          id="edit-webinar-notes"
                          rows={6}
                          value={notes}
                          onChange={(event) => setNotes(event.target.value)}
                        />
                      </LegacyInputGroup>
                    </LegacyCell>
                  </LegacyRow>

                  <LegacyRow>
                    <LegacyCell colSpan={2}>
                      <LegacySubmitRow colors={colors}>
                        <input type="submit" value="Submit" />
                        <LegacyCancelLink
                          type="button"
                          colors={colors}
                          onClick={handleCancelEdit}
                        >
                          Cancel Edit
                        </LegacyCancelLink>
                      </LegacySubmitRow>
                    </LegacyCell>
                  </LegacyRow>
                </tbody>
              </LegacyTable>
            </LegacyForm>
          </LegacyOuter>
        )}

        {view === "regs" && (
          <>
            <h1 style={{ color: colors.labelText }}>
              Registrations for “{currentWebinar?.name}”
            </h1>
            <TableWrapper>
              <Table colors={colors}>
                <thead>
                  <tr>
                    <th>Organization Name</th>
                    <th>Contact Name</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map(r => (
                    <tr key={r.web_reg_id}>
                      <td>{r.org_name}</td>
                      <td>{r.user_name}</td>
                      <td>{r.email}</td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </>
        )}
      </View>

      <div style={{ clear: "both" }} />
    </Container>
  );
}
